// Lightweight markdown → pdfmake content parser for the module PDF export (headings,
// bold/italic/code, links, bullet/numbered lists, tables, fenced code). A deliberate subset — not full
// markdown. Kept free of pdfmake/auth runtime imports (only pdfmake *types*, which erase) so it
// unit-tests directly; moduleToPdf composes it with images and the doc definition.
//
// DUPLICATED: this is a verbatim copy of admin-portal-v5
// src/features/learningModule/utils/markdownToPdfContent.ts. Both portals must render module PDFs
// identically, so the copy is intentional — any change here MUST be mirrored there (and vice
// versa; admin is the source of truth).
// TODO(monorepo): when we move to the monorepo starter, extract this + moduleToPdf into a shared
// package (e.g. @hopdrive/learning-center) consumed by both portals, and delete both copies.

import type { Content, Margins } from 'pdfmake/interfaces';

// One top-level list item plus any nested sub-lists. Nesting is one level deep — a lightweight
// subset, enough for "a bullet list under a numbered step" — rendered stacked under the item text.
type NestedList = { ordered: boolean; items: { text: Content[] }[] };
type ListItem = { text: Content[]; children: NestedList[] };
type OpenList = { ordered: boolean; items: ListItem[]; loose: boolean };

// Loose lists (items separated by blank lines) get a little breathing room between items so the
// spacing the author added in the editor survives the export.
const LOOSE_ITEM_MARGIN: Margins = [0, 0, 0, 4];
const LIST_MARGIN: Margins = [0, 2, 0, 6];
const NESTED_MARGIN: Margins = [0, 2, 0, 2];
const TABLE_MARGIN: Margins = [0, 4, 0, 8];

// ── glyph substitution ──────────────────────────────────────────────────────────
//
// The PDF is rendered in Roboto, and the copy of Roboto bundled in pdfmake's VFS maps only ~927
// codepoints — Latin-1, Greek/Cyrillic, and basic punctuation. It has NO emoji, and also none of
// the dingbats authors reach for as substitutes: ✓ (U+2713), ✗ (U+2717), → (U+2192), ★ (U+2605)
// and ℹ (U+2139) are all absent. pdfmake has no font-fallback mechanism, so an unmapped codepoint
// renders as a blank box — the symbol silently disappears from the export.
//
// So the ones authors actually use are mapped to sequences the bundled font can definitely draw.
// Bracketed words follow the convention this parser already uses for content it can't render
// faithfully ("[Image: …]"), so a reader can tell a symbol stood there rather than reading a
// stray word. Anything unmapped that's still outside the font is dropped (see stripUnsupported)
// rather than left as an empty box.
// Every pattern carries the `u` flag on purpose. Without it a character class matches UTF-16 code
// UNITS, so an astral emoji contributes its two surrogates as separate members — '\uD83D' from 🗹
// then matches the high surrogate of every other 🙂-plane emoji (🚗 became "[Yes]" + an orphan
// surrogate before this was caught). With `u`, classes match whole code points.
const GLYPH_REPLACEMENTS: [RegExp, string][] = [
  [/[✅✔✓☑☐\u{1F5F9}]️?/gu, '[Yes]'], // ✅ ✔ ✓ ☑ ☐ 🗹
  [/[❌❎✖✗✘✕]️?/gu, '[No]'], //     ❌ ❎ ✖ ✗ ✘ ✕
  [/[⚠‼❗❕]️?/gu, '[!]'], //                  ⚠ ‼ ❗ ❕
  [/[ℹ❓❔]️?/gu, '[i]'], //                        ℹ ❓ ❔
  [/[\u{1F31F}⭐★☆]️?/gu, '*'], //                 🌟 ⭐ ★ ☆
  [/[→⇒➡➔]️?/gu, '->'], //                   → ⇒ ➡ ➔
  [/[←⇐⬅]️?/gu, '<-'], //                         ← ⇐ ⬅
  [/[‣◦▪▫□]️?/gu, '•'], //                   ‣ ◦ ▪ ▫ □ → • (in Roboto)
  // Box drawing and geometric shapes — authors draw flow diagrams with these inside fenced blocks
  // (e.g. the lifecycle diagram in docs/creating-a-learning-module.md: "Draft ──Publish──▶ …").
  // None of U+2500–U+257F or U+25A0–U+25FF is in the bundled font, so the whole diagram used to
  // come out as rows of blank boxes. Mapped 1:1 to ASCII line art so it still reads as a diagram.
  [/[─━┈┉╌╍═]/gu, '-'], //          ─ ━ ┈ ┉ ╌ ╍ ═
  [/[│┃┆┇╎╏║]/gu, '|'], //          │ ┃ ┆ ┇ ╎ ╏ ║
  [/[┌┐└┘├┤┬┴┼╭╮╰╯╔╗╚╝╠╣╦╩╬]/gu, '+'], // corners, tees, crosses
  [/[▶►▸➤]️?/gu, '>'], //                  ▶ ► ▸ ➤
  [/[◀◄◂]️?/gu, '<'], //                        ◀ ◄ ◂
  [/[▲▴]️?/gu, '^'], //                          ▲ ▴
  [/[▼▾]️?/gu, 'v'], //                          ▼ ▾
  [/[●■◆]️?/gu, 'o'], //                        ● ■ ◆
];

// Symbols the map above doesn't name, by block: arrows, misc-technical + dingbats, misc symbols &
// arrows, the emoji planes, and the variation-selector / keycap combiners. All absent from the
// bundled Roboto, so they're dropped rather than left to render as blank boxes. Every substitution
// target above (• — × ≥) sits below U+2190 and is therefore untouched by these ranges.
// Matched as a run together with any spaces on either side, so substituteGlyphs can decide whether
// to keep one of them (see there).
const RESIDUAL_PICTOGRAPHS_RUN =
  / *[←-⇿⌀-➿⬀-⯿️⃣\u{1F000}-\u{1FAFF}]+ */gu;

/**
 * Replace symbols the embedded font can't draw with ones it can. Applied to code as well as prose:
 * "keep code byte-exact" sounds right but doesn't hold here, because an unrenderable byte isn't
 * preserved in the PDF — it renders as nothing at all. A fenced ASCII diagram is the case that
 * makes this concrete: every line-art character is outside the font, so the diagram arrived as rows
 * of blank boxes. A visible ASCII stand-in beats an invisible original.
 */
export function substituteGlyphs(text: string): string {
  let out = text;
  for (const [re, sub] of GLYPH_REPLACEMENTS) out = out.replace(re, sub);
  // A dropped symbol takes one adjacent space with it, so "🚗 Vehicle" reads "Vehicle" rather than
  // " Vehicle" — but only the space it was actually next to. Deliberately not a trim(): a plain
  // text run legitimately carries leading/trailing spaces (the " below" in "![](x) below"), and
  // trimming those would weld words together in the rebuilt line.
  return out.replace(RESIDUAL_PICTOGRAPHS_RUN, (m, offset: number, whole: string) => {
    // Whitespace the match swallowed at the start of the line is indentation, not padding around
    // the symbol — give it back, or a fenced block loses the indent preserveLeadingSpaces exists
    // to keep. No general multi-space collapse for the same reason: runs of spaces are meaningful
    // inside a code block and in the columns of an ASCII diagram.
    const lead = /^ */.exec(m)?.[0] ?? '';
    if (offset === 0) return lead;
    return offset + m.length === whole.length ? '' : ' ';
  });
}

/** Parse inline **bold**, *italic*, `code`, and [links](url) into pdfmake text runs. */
export function parseInline(text: string): Content[] {
  const runs: Content[] = [];
  // Emphasis delimiters must hug non-space text (…**bold**… / …*italic*…) so a stray literal
  // asterisk — "2 * 3", a footnote marker — doesn't start an italic run that swallows text up
  // to the next asterisk. Content may contain inner spaces but not leading/trailing ones.
  const emph = '(?:[^*\\s]|[^*\\s][^*]*[^*\\s])';
  // The image alternative (`!\[...\]\(...\)`) must precede the link one so an inline image isn't
  // mis-tokenized as a link — its URL is usually a private-bucket asset that 403s if followed
  // (standalone images are embedded upstream in bodyToContent; an inline one can't be, so it
  // degrades to its alt text rather than a dead clickable link).
  const re = new RegExp(`(\\*\\*${emph}\\*\\*|\\*${emph}\\*|\`[^\`]+\`|!\\[[^\\]]*\\]\\([^)]+\\)|\\[[^\\]]+\\]\\([^)]+\\))`, 'g');
  let last = 0;
  let m: RegExpExecArray | null;
  // Plain-text runs are pushed only when non-empty: a segment that was nothing but an unmapped
  // emoji substitutes to '', and an empty run would add a stray gap to the line.
  const pushProse = (s: string, style?: Record<string, unknown>) => {
    const sub = substituteGlyphs(s);
    if (sub) runs.push({ text: sub, ...style });
  };
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) pushProse(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) pushProse(tok.slice(2, -2), { bold: true });
    else if (tok.startsWith('`')) pushProse(tok.slice(1, -1), { background: '#f2f2f3' });
    else if (tok.startsWith('*')) pushProse(tok.slice(1, -1), { italics: true });
    else if (tok.startsWith('!')) {
      // Always leave a marker (never silently drop the token): a bare `![](url)` with no alt still
      // becomes "[Image]" so the paragraph doesn't read as if text is missing.
      const alt = /!\[([^\]]*)\]/.exec(tok)?.[1] ?? '';
      runs.push({ text: alt ? `[Image: ${alt}]` : '[Image]', italics: true });
    } else {
      const link = /\[([^\]]+)\]\(([^)]+)\)/.exec(tok);
      // The URL is left byte-exact — only the visible label is substituted.
      if (link) runs.push({ text: substituteGlyphs(link[1]), link: link[2], color: '#4068a0', decoration: 'underline' });
    }
    last = m.index + tok.length;
  }
  if (last < text.length) pushProse(text.slice(last));
  return runs.length ? runs : [{ text: substituteGlyphs(text) }];
}

// ── tables ──────────────────────────────────────────────────────────────────────

type CellAlign = 'left' | 'center' | 'right';

/**
 * Split one table row into cells on unescaped, non-code pipes. A `\|` is a literal pipe (GFM's
 * escape) and a pipe inside a `code span` is content, not a delimiter — splitting naively on '|'
 * would break both. Leading/trailing delimiters are optional, per GFM.
 */
export function splitRow(line: string): string[] {
  const cells: string[] = [];
  let cur = '';
  let inCode = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '\\' && line[i + 1] === '|') {
      cur += '|';
      i++;
      continue;
    }
    if (ch === '`') inCode = !inCode;
    if (ch === '|' && !inCode) {
      cells.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  cells.push(cur);
  // Drop the empty cells produced by an optional leading/trailing pipe.
  if (cells.length && cells[0].trim() === '') cells.shift();
  if (cells.length && cells[cells.length - 1].trim() === '') cells.pop();
  return cells.map(c => c.trim());
}

/** The `|---|:--:|--:|` row under a table header. Returns per-column alignment, or null. */
function parseDelimiterRow(line: string): CellAlign[] | null {
  if (!line.includes('-') || !line.includes('|')) return null;
  const cells = splitRow(line);
  if (!cells.length) return null;
  const aligns: CellAlign[] = [];
  for (const c of cells) {
    if (!/^:?-{1,}:?$/.test(c)) return null;
    aligns.push(c.startsWith(':') && c.endsWith(':') ? 'center' : c.endsWith(':') ? 'right' : 'left');
  }
  return aligns;
}

/**
 * Build the pdfmake table. Every row is normalized to the header's column count because pdfmake
 * requires a rectangular body: a short row is padded with empty cells, and a long row's surplus is
 * folded into its last cell so no authored text is lost.
 */
function tableToContent(header: string[], aligns: CellAlign[], rows: string[][]): Content {
  const cols = header.length;
  const normalize = (cells: string[]): string[] => {
    if (cells.length === cols) return cells;
    if (cells.length < cols) return [...cells, ...Array(cols - cells.length).fill('')];
    return [...cells.slice(0, cols - 1), cells.slice(cols - 1).join(' ')];
  };
  const body: Content[][] = [
    header.map((h, i) => ({ text: parseInline(h), bold: true, alignment: aligns[i] ?? 'left', fillColor: '#f4f6f8' })),
    ...rows.map(r =>
      normalize(r).map((c, i) => ({ text: parseInline(c), alignment: aligns[i] ?? 'left' })),
    ),
  ];
  return {
    table: { headerRows: 1, widths: Array(cols).fill('*'), body },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => '#d8dbe0',
      vLineColor: () => '#d8dbe0',
      paddingTop: () => 4,
      paddingBottom: () => 4,
      paddingLeft: () => 6,
      paddingRight: () => 6,
    },
    margin: TABLE_MARGIN,
  };
}

/** Convert a markdown string to an ordered list of pdfmake content blocks. */
export function markdownToContent(md: string): Content[] {
  const out: Content[] = [];
  let list: OpenList | null = null;
  // The open nested list under the current list's last item, so consecutive indented items of the
  // same kind group together; reset whenever a top-level item or a non-list line arrives.
  let nested: NestedList | null = null;

  const listToContent = (l: OpenList): Content => {
    const items: Content[] = l.items.map(it => {
      if (it.children.length === 0) {
        return l.loose ? { text: it.text, margin: LOOSE_ITEM_MARGIN } : { text: it.text };
      }
      // Item with a sub-list: stack the text over its nested list(s) so the parent marker (the
      // number/bullet) stays aligned to the item and the parent numbering continues across it.
      const sub: Content[] = it.children.map(nl =>
        nl.ordered ? { ol: nl.items, margin: NESTED_MARGIN } : { ul: nl.items, margin: NESTED_MARGIN },
      );
      const stacked: Content = { stack: [{ text: it.text }, ...sub] };
      return l.loose ? { ...stacked, margin: LOOSE_ITEM_MARGIN } : stacked;
    });
    return l.ordered ? { ol: items, margin: LIST_MARGIN } : { ul: items, margin: LIST_MARGIN };
  };

  const flush = () => {
    if (!list) return;
    out.push(listToContent(list));
    list = null;
    nested = null;
  };
  // Fence markers never render; fenced content renders verbatim (no inline markdown parsing),
  // in the inline-code background. Same close rule as markdownSections: same char, >= length.
  let fence: { char: string; len: number } | null = null;
  // A blank line while a list is open *might* make it loose — but only if another item follows.
  // Deferred (not committed on the blank line itself) so a single trailing blank before a
  // following paragraph doesn't wrongly add inter-item spacing to a tight list.
  let pendingLooseBlank = false;
  // Indexed rather than for-of: a table is only a table if the line AFTER the header is a
  // delimiter row, so detection needs one line of lookahead.
  const lines = (md || '').split('\n');
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trimEnd();
    const f = /^\s{0,3}(`{3,}|~{3,})/.exec(line);
    if (f && !fence) {
      // Opening fence.
      flush();
      fence = { char: f[1][0], len: f[1].length };
      continue;
    }
    if (f && fence && f[1][0] === fence.char && f[1].length >= fence.len) {
      // Closing fence (same char, at least as long as the opener).
      fence = null;
      continue;
    }
    if (fence) {
      // Inside a fence — no inline markdown parsing, but glyphs still get substituted (see
      // substituteGlyphs: line art in a diagram is unrenderable otherwise). This includes a line
      // that merely *looks* like a fence marker but isn't a valid close (different char, or a
      // shorter run); it must not be dropped.
      out.push({
        text: substituteGlyphs(raw),
        background: '#f2f2f3',
        margin: [0, 0, 0, 2],
        preserveLeadingSpaces: true,
      });
      continue;
    }
    if (!line.trim()) {
      // A blank line inside a list does NOT end it: items separated by blank lines form one
      // (loose) list, so ordered numbering stays continuous (1., 2., 3.) instead of restarting
      // each item at 1. Only flag it pending — the list becomes loose only if another item
      // actually follows (see the list-item branch); a trailing blank before a paragraph leaves
      // it tight. Outside a list there's nothing to flush.
      if (list) pendingLooseBlank = true;
      continue;
    }
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      flush();
      // h4–h6 all render as the smallest heading style — a lightweight subset, not full markdown.
      out.push({ text: parseInline(h[2]), style: h[1].length === 1 ? 'h1' : h[1].length === 2 ? 'h2' : 'h3' });
      continue;
    }
    // A GFM pipe table: a header row followed by a delimiter row. Both are required — a lone line
    // with pipes in it is just prose (and used to render as one, delimiter row and all, which is
    // the bug this fixes). Body rows run until a line without a pipe, a blank, or the end.
    if (line.includes('|') && i + 1 < lines.length) {
      const aligns = parseDelimiterRow(lines[i + 1].trimEnd());
      if (aligns) {
        flush();
        const header = splitRow(line);
        const rows: string[][] = [];
        let j = i + 2;
        for (; j < lines.length; j++) {
          const next = lines[j].trimEnd();
          if (!next.trim() || !next.includes('|')) break;
          rows.push(splitRow(next));
        }
        out.push(tableToContent(header, aligns, rows));
        i = j - 1; // the outer loop's i++ lands on the first line after the table
        continue;
      }
    }
    // A list item, possibly indented. An indented (≥2 spaces) marker nests under the current list's
    // last item instead of terminating the list — so a sub-list inside a numbered step doesn't
    // break the parent's numbering (the bug this fixes). Marker: '-'/'*' bullet, or 'N.' ordered.
    const li = /^(\s*)([-*]|\d+\.)\s+(.*)$/.exec(line);
    if (li) {
      const indent = li[1].length;
      const ordered = li[2].endsWith('.');
      const text = parseInline(li[3]);
      if (indent >= 2 && list && list.items.length > 0) {
        const parent = list.items[list.items.length - 1];
        if (!nested || nested.ordered !== ordered) {
          nested = { ordered, items: [] };
          parent.children.push(nested);
        }
        nested.items.push({ text });
        pendingLooseBlank = false; // a blank absorbed by nested content doesn't loosen the parent
        continue;
      }
      // Top-level item: ends any open nested list; a marker-kind switch starts a fresh list; a
      // blank line between two items of the same list marks it loose (deferred until now).
      nested = null;
      if (!list || list.ordered !== ordered) {
        flush();
        list = { ordered, items: [], loose: false };
      } else if (pendingLooseBlank) {
        list.loose = true;
      }
      pendingLooseBlank = false;
      list.items.push({ text, children: [] });
      continue;
    }
    flush();
    out.push({ text: parseInline(line), margin: [0, 0, 0, 6] });
  }
  flush();
  return out;
}
